import React from 'react';
import { numeroContratoComAno as numeroComAno } from '../services/execucaoFinanceiraService';
import type { AtoLegalEmenda, ExecucaoContrato } from '../services/execucaoFinanceiraService';

type Props = {
  contratos: ExecucaoContrato[];
  atosLegais: AtoLegalEmenda[];
};

const money = (value: number) => value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const date = (value?: string) => value ? new Date(`${value}T12:00:00`).toLocaleDateString('pt-BR') : '—';
const hoje = () => new Date().toISOString().slice(0, 10);

const vigenciaEncerrada = (contrato: ExecucaoContrato) => Boolean(contrato.vigenciaFim && contrato.vigenciaFim < hoje());

const surface = 'rounded-2xl';

/**
 * Contratos (inclui o Termo de Fomento) e atos legais que o Coplan liga a esta emenda.
 * Somente leitura: os dados vêm da importação do Coplan (datalake) e não são editáveis aqui.
 */
const ContratosEAtosLegaisSection: React.FC<Props> = ({ contratos, atosLegais }) => {
  if (contratos.length === 0 && atosLegais.length === 0) return null;

  return (
    <div className="space-y-5">
      {contratos.length > 0 && (
        <div data-testid="execucao-contratos">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Contratos e Termo de Fomento</h3>
          <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">Vinculados pelo empenho no Coplan. Um contrato pode atender mais de uma emenda.</p>
          <div className={`mt-3 divide-y divide-slate-200 overflow-hidden border border-slate-200 dark:divide-slate-700 dark:border-slate-700 ${surface}`}>
            {contratos.map((contrato) => (
              <div key={contrato.id} className="bg-white px-4 py-3 dark:bg-slate-900/40">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`rounded-full px-2 py-0.5 text-[0.6875rem] font-semibold ${contrato.instrumento === 'TERMO_FOMENTO'
                      ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                      : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'}`}>
                      {contrato.instrumento === 'TERMO_FOMENTO' ? 'Termo de Fomento' : 'Contrato'}
                    </span>
                    <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">{numeroComAno(contrato.numero, contrato.ano)}</span>
                    {vigenciaEncerrada(contrato) && (
                      <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[0.6875rem] font-semibold text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">Vigência encerrada</span>
                    )}
                  </div>
                  <div className="text-right text-xs text-slate-500 dark:text-slate-400">
                    <p>Empenhado nesta emenda: <strong className="text-slate-800 dark:text-slate-100">{money(contrato.empenhadoLiquido)}</strong></p>
                    {contrato.valor != null && <p>Valor do contrato: {money(contrato.valor)}</p>}
                  </div>
                </div>
                {contrato.objeto && <p className="mt-1.5 text-xs text-slate-600 dark:text-slate-300">{contrato.objeto}</p>}
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  Vigência: {date(contrato.vigenciaInicio)} a {date(contrato.vigenciaFim)}
                  {contrato.assinatura ? ` · Assinado em ${date(contrato.assinatura)}` : ''}
                </p>
                {contrato.processoCompraNumero && (
                  <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                    Processo de compra: {numeroComAno(contrato.processoCompraNumero, contrato.processoCompraAno)}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {atosLegais.length > 0 && (
        <div data-testid="execucao-atos-legais">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Atos legais da dotação</h3>
          <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">Leis e atos que autorizam a dotação orçamentária desta emenda (crédito adicional e remanejamento).</p>
          <div className={`mt-3 divide-y divide-slate-200 overflow-hidden border border-slate-200 dark:divide-slate-700 dark:border-slate-700 ${surface}`}>
            {atosLegais.map((ato) => (
              <div key={ato.id} className="bg-white px-4 py-3 dark:bg-slate-900/40">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">Ato legal nº {numeroComAno(ato.numero, ato.ano)}</span>
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[0.6875rem] font-semibold text-slate-600 dark:bg-slate-700 dark:text-slate-300">Natureza {ato.natureza}</span>
                  {ato.dataRevogacao && (
                    <span className="rounded-full bg-red-100 px-2 py-0.5 text-[0.6875rem] font-semibold text-red-700 dark:bg-red-900/30 dark:text-red-300">Revogado em {date(ato.dataRevogacao)}</span>
                  )}
                </div>
                {ato.ementa && <p className="mt-1 text-xs text-slate-600 dark:text-slate-300">{ato.ementa}</p>}
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  Publicado em {date(ato.dataPublicacao)}
                  {ato.linkOficial && (
                    <>
                      {' · '}
                      <a href={ato.linkOficial} target="_blank" rel="noopener noreferrer" className="font-semibold text-sky-700 underline dark:text-sky-300">Texto oficial</a>
                    </>
                  )}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ContratosEAtosLegaisSection;
